import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, Crown, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [error, setError] = useState(null);

  const paymentId = searchParams.get('payment_id');

  useEffect(() => {
    if (paymentId && token) {
      activateSubscription();
    } else if (!paymentId) {
      setError('Invalid payment reference');
      setLoading(false);
    }
  }, [paymentId, token]);

  const activateSubscription = async () => {
    setActivating(true);
    try {
      // First try to activate manually (in case ITN hasn't processed yet)
      const response = await axios.post(
        `${API}/subscription/activate`,
        { payment_id: paymentId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      setSubscriptionData(response.data);
      toast.success('Subscription activated successfully!');
    } catch (error) {
      // If activation fails, check status
      try {
        const statusResponse = await axios.get(`${API}/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (statusResponse.data.status === 'active') {
          setSubscriptionData({
            success: true,
            subscription_ends_at: statusResponse.data.subscription_ends_at
          });
        } else {
          setError('Payment is being processed. Please check back in a few minutes.');
        }
      } catch (statusError) {
        setError('Unable to verify subscription. Please contact support.');
      }
    } finally {
      setLoading(false);
      setActivating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Activating your subscription...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="h-8 w-8 text-amber-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Processing Payment</h1>
            <p className="text-zinc-400 mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 py-3 rounded-lg font-semibold transition-colors"
              >
                Check Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Success Animation */}
        <div className="relative mb-8">
          <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto animate-pulse">
            <CheckCircle2 className="h-12 w-12 text-green-400" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-zinc-900" />
          </div>
        </div>

        {/* Success Message */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Crown className="h-6 w-6 text-amber-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
              Cerebro Core
            </span>
          </div>

          <h1 className="text-2xl font-bold text-white mb-2">
            Welcome to Cerebro Core!
          </h1>
          <p className="text-zinc-400 mb-6">
            Your annual subscription is now active. You have unlimited access to all courses and features.
          </p>

          {subscriptionData?.subscription_ends_at && (
            <div className="bg-zinc-800 rounded-lg p-4 mb-6">
              <p className="text-sm text-zinc-400">Subscription valid until</p>
              <p className="text-lg font-semibold text-white">
                {new Date(subscriptionData.subscription_ends_at).toLocaleDateString('en-ZA', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          )}

          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
          >
            Start Learning
            <ArrowRight className="h-5 w-5" />
          </button>

          <p className="text-xs text-zinc-500 mt-4">
            A confirmation email has been sent to {user?.email}
          </p>
        </div>
      </div>
    </div>
  );
}
